import {
  EstimateAllocationConfig,
  EstimateAllocationResult,
  ValidationResult,
} from "./types";
import { VirtualOrderBlockStatus } from "../../assignSupplierUtils";
import { OrderItem } from "../../orderUtils";
import services from "../../services";
import {
  NormalizedDeliverySelection,
  EnrichedSupplierForDelivery,
  SupplierPartForDelivery,
} from "../../../../components/DeliveryOptions/types";
import {
  OrderHandlerAllocationType,
  OrderStatus,
  OrderTypes,
  TransactionType,
} from "../../enums";
import { getCurrentUTCTimestamp } from "../../../../utils/dateResolvers";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { extractErrorMessage } from "../../utils/orderItemsActionUtils";
import {
  OrderItemActionType,
  CUSTOMER_ACTION_TO_API_ACTION,
} from "../../types/orderItemsActionTypes";

dayjs.extend(utc);
dayjs.extend(timezone);

const IST_TIMEZONE = "Asia/Kolkata";

const BLOCK_REASONS = {
  CUSTOMER_HOLD:
    "Order is currently on customer hold. Please resume the order before assigning suppliers.",
  DELIVERED: "Cannot assign supplier to delivered order.",
  CANCELLED: "Cannot assign supplier to cancelled order.",
  FROZEN: "Order is frozen and cannot be modified.",
} as const;

export class EstimateAllocatorService {
  static async allocate(
    allocationRequest: EstimateAllocationConfig,
    isCustomerOrderFreezed: boolean,
  ): Promise<EstimateAllocationResult> {
    // =========================================================================
    // STEP 1: Validation
    // =========================================================================
    const allocationRequestValidation =
      EstimateAllocatorService.validateAllocationRequest(allocationRequest);
    if (!allocationRequestValidation.isValid) {
      return {
        success: false,
        message:
          allocationRequestValidation.message || "Invalid allocation request",
      };
    }

    const blockStatus = EstimateAllocatorService.validateOrderBlockStatus(
      allocationRequest.sourceOrder,
    );
    if (blockStatus.isBlocked) {
      return {
        success: false,
        message: blockStatus.reason || "Order is blocked",
      };
    }

    const orderData = allocationRequest.sourceOrder;
    const splitSelections = allocationRequest.splitSelections!;
    const splitData = allocationRequest.splitData!;
    const cptGivenTime = allocationRequest.cptGivenTime!;
    const orderNo = orderData.orderNo;

    // =========================================================================
    // STEP 2: Initialize working state with structuredClone for immutability
    // =========================================================================
    let workingOrderItems: OrderItem[] = structuredClone(
      allocationRequest.orderItems,
    );
    let workingOrderEta: Array<{
      etaGivenTime: string;
      eta: string;
      reason?: string;
    }> = structuredClone(allocationRequest.orderEta);
    let isEtaUpdated = false;

    // =========================================================================
    // STEP 3: Process delivery options actions via new API
    // Order: Hold → Cancel → Buy Time
    // =========================================================================
    const holdSelections = splitSelections.filter(
      (s) => s.customerAction === "hold",
    );
    if (holdSelections.length > 0) {
      const holdResult =
        await EstimateAllocatorService.processActionOnOrderItems(
          orderNo,
          CUSTOMER_ACTION_TO_API_ACTION.hold,
          holdSelections,
          splitData,
        );
      if (!holdResult.success) {
        return {
          success: false,
          message: `Failed to hold items: ${holdResult.error}`,
        };
      }

      workingOrderItems = EstimateAllocatorService.applyHoldToItems(
        workingOrderItems,
        holdSelections,
        splitData,
      );
    }

    const cancelSelections = splitSelections.filter(
      (s) => s.customerAction === "cancel",
    );
    if (cancelSelections.length > 0) {
      const cancelResult =
        await EstimateAllocatorService.processActionOnOrderItems(
          orderNo,
          CUSTOMER_ACTION_TO_API_ACTION.cancel,
          cancelSelections,
          splitData,
        );
      if (!cancelResult.success) {
        return {
          success: false,
          message: `Failed to cancel items: ${cancelResult.error}`,
        };
      }

      // Keep local state computation as safeguard
      workingOrderItems = EstimateAllocatorService.applyCancelToItems(
        workingOrderItems,
        cancelSelections,
        splitData,
      );
    }

    const buyTimeSelections = splitSelections.filter(
      (s) => s.customerAction === "buytime",
    );
    if (buyTimeSelections.length > 0) {
      const buyTimeResult = EstimateAllocatorService.computeBuyTimeEta(
        workingOrderEta,
        buyTimeSelections,
        cptGivenTime,
      );
      if (buyTimeResult.isEtaUpdated) {
        workingOrderEta = buyTimeResult.updatedEta;
        isEtaUpdated = true;
      }
    }

    const confirmSelections = splitSelections.filter(
      (s) => s.customerAction === "confirm",
    );

    // =========================================================================
    // STEP 4: Update source order with ETA (only if changed)
    // Moved here to ensure Buy Time ETA is saved even if no confirms exist.
    // =========================================================================
    if (isEtaUpdated) {
      try {
        await EstimateAllocatorService.updateSourceOrder(orderData._id, {
          orderEta: workingOrderEta,
        });
      } catch (error: any) {
        return {
          success: false,
          message: `Failed to update source order: ${error.message || error}`,
          error,
        };
      }
    }

    if (confirmSelections.length === 0 || isCustomerOrderFreezed) {
      if (isCustomerOrderFreezed && confirmSelections.length > 0) {
        return {
          success: false,
          message:
            "Customer is Blocked. Please unblock the customer before confirming items.",
        };
      }

      return {
        success: true,
        message: "Successfully processed actions for the estimate.",
      };
    }

    // =========================================================================
    // STEP 4.1: Validate transactionType is provided for order creation
    // transactionType is required when creating child orders for confirmed suppliers
    // =========================================================================
    if (!allocationRequest.transactionType) {
      return {
        success: false,
        message:
          "Transaction type is required for creating child orders with confirmed suppliers.",
      };
    }

    const transactionType: string = allocationRequest.transactionType;

    // =========================================================================
    // STEP 5: Create child orders for confirmed suppliers
    // Note: We use splitData to get the actual parts for each supplier split,
    // NOT supplier.selectedParts which contains bucket capacity (not split quantities)
    //
    // We use availableForAllocation (with buy time applied) for child order creation
    // to ensure buy time quantities are not allocated. workingOrderItems stays unchanged
    // for the final source order update (buy time parts keep their remainingDecisionQuantity).
    // =========================================================================
    const createdOrders: any[] = [];

    // Create availableForAllocation with buy time applied for defensive tracking
    // This ensures buy time quantities cannot be allocated to child orders
    let availableForAllocation: OrderItem[] =
      structuredClone(workingOrderItems);
    if (buyTimeSelections.length > 0) {
      availableForAllocation = EstimateAllocatorService.applyBuyTimeToItems(
        availableForAllocation,
        buyTimeSelections,
        splitData,
      );
    }

    // Filter suppliers to only those with confirm selections
    const suppliersToProcess = allocationRequest.suppliers.filter((supplier) =>
      confirmSelections.some(
        (selection) => selection.supplierId === supplier.supplierId,
      ),
    );

    for (const supplier of suppliersToProcess) {
      // Find the corresponding split from splitData
      const supplierSplit = splitData.find(
        (s) => s.supplierId === supplier.supplierId,
      );

      if (!supplierSplit) {
        console.warn(
          `Supplier ${supplier.supplierName} (${supplier.supplierId}) exists in suppliersToProcess but not in splitData. Skipping.`,
        );
        continue;
      }

      try {
        // Map split parts to order items (using availableForAllocation for defensive tracking)
        const childOrderItems =
          EstimateAllocatorService.mapSplitPartsToOrderItems(
            availableForAllocation,
            supplierSplit.parts,
          );

        // Skip if no items to allocate (guard already capped quantities)
        if (childOrderItems.length === 0) {
          console.warn(
            `No items available to allocate for supplier ${supplier.supplierName} (${supplier.supplierId}). Skipping.`,
          );
          continue;
        }
        // Get confirm selection for this supplier
        const confirmSelection = confirmSelections.find(
          (s) => s.supplierId === supplier.supplierId,
        );

        const deliveryCost = confirmSelection?.deliveryCost;
        const deliveryMode = confirmSelection?.deliveryMode;
        const childOrderResponse =
          await EstimateAllocatorService.createChildOrder(
            allocationRequest.sourceOrder,
            childOrderItems,
            transactionType,
            deliveryCost,
            deliveryMode,
          );

        const childOrderId = childOrderResponse._id;
        const childOrderNo = childOrderResponse.orderNo;

        // Build orderETA from System: CPT
        const cptGivenTimeIST = dayjs(cptGivenTime)
          .utc()
          .tz(IST_TIMEZONE)
          .format("YYYY-MM-DDTHH:mm:ss");
        const cptMinIST = confirmSelection?.cptMin
          ? dayjs(confirmSelection.cptMin)
              .utc()
              .tz(IST_TIMEZONE)
              .format("YYYY-MM-DDTHH:mm:ss")
          : cptGivenTimeIST;

        const orderETA = [
          {
            etaGivenTime: cptGivenTimeIST,
            eta: cptMinIST,
            reason: "System: CPT",
          },
        ];

        const vehicleType = confirmSelection?.vehicleType;

        await EstimateAllocatorService.updateOrderInfo(
          childOrderId,
          allocationRequest.sourceOrder,
          orderETA,
          transactionType,
          allocationRequest.discount,
          allocationRequest.creditNotePercentage,
          confirmSelection?.cptSlab,
          vehicleType,
        );

        await EstimateAllocatorService.acknowledgeOrder(childOrderId);

        await EstimateAllocatorService.allocateToSupplier(
          childOrderId,
          supplier.supplierId,
        );

        await EstimateAllocatorService.updateMetaInfo(
          childOrderId,
          allocationRequest.sourceOrder.metaInfo,
          true,
        );

        createdOrders.push({
          orderId: childOrderId,
          orderNo: childOrderNo,
          supplierId: supplier.supplierId,
          supplierName: supplier.supplierName,
          isAllocated: true,
          orderItems: childOrderResponse.orderItems,
        });

        // Reduce availableForAllocation for next supplier (defensive tracking)
        availableForAllocation = EstimateAllocatorService.reduceAvailableItems(
          availableForAllocation,
          childOrderItems,
        );
      } catch (error: any) {
        return {
          success: false,
          message: `Failed to create order for supplier ${
            supplier.supplierName
          }: ${error.message || error}`,
          error,
        };
      }
    }

    // =========================================================================
    // STEP 6: Handle remaining items (create remaining order if confirmed)
    // Note: We use splitData to get the actual parts in the remaining split,
    // NOT remainingParts which could incorrectly include buy time parts
    // =========================================================================
    let remainingOrder: any;
    const remainingPartsConfirmSelection = confirmSelections.find(
      (s) => s.supplierId === null || s.supplierId === undefined,
    );

    // Get the actual remaining items split from splitData
    const remainingItemsSplit = splitData.find(
      (s) => s.supplierId === null || s.supplierId === undefined,
    );

    if (remainingPartsConfirmSelection && remainingItemsSplit) {
      // Map the remaining split parts to order items format (using availableForAllocation)
      const remainingItemsForOrder =
        EstimateAllocatorService.mapSplitPartsToOrderItems(
          availableForAllocation,
          remainingItemsSplit.parts,
        );

      if (remainingItemsForOrder.length > 0) {
        try {
          const cptGivenTimeIST = dayjs(cptGivenTime)
            .utc()
            .tz(IST_TIMEZONE)
            .format("YYYY-MM-DDTHH:mm:ss");
          const cptMinIST = remainingPartsConfirmSelection.cptMin
            ? dayjs(remainingPartsConfirmSelection.cptMin)
                .utc()
                .tz(IST_TIMEZONE)
                .format("YYYY-MM-DDTHH:mm:ss")
            : cptGivenTimeIST;

          const remainingOrderETA = [
            {
              etaGivenTime: cptGivenTimeIST,
              eta: cptMinIST,
              reason: "System: CPT",
            },
          ];

          const remainingOrderDeliveryCost =
            remainingPartsConfirmSelection.deliveryCost;
          const remainingOrderPartsCptSlab =
            remainingPartsConfirmSelection.cptSlab;

          const remainingOrderVehicleType =
            remainingPartsConfirmSelection.vehicleType;

          const remainingOrderDeliveryMode =
            remainingPartsConfirmSelection.deliveryMode;

          const remainingOrderResponse =
            await EstimateAllocatorService.createRemainingOrder(
              allocationRequest.sourceOrder,
              remainingItemsForOrder,
              remainingOrderETA,
              transactionType,
              allocationRequest.discount,
              allocationRequest.creditNotePercentage,
              remainingOrderDeliveryCost,
              remainingOrderPartsCptSlab,
              remainingOrderVehicleType,
              remainingOrderDeliveryMode,
            );

          remainingOrder = {
            orderId: remainingOrderResponse._id,
            orderNo: remainingOrderResponse.orderNo,
            isAllocated: false,
            orderItems: remainingOrderResponse.orderItems,
          };
        } catch (error: any) {
          return {
            success: false,
            message: `Failed to create remaining order: ${
              error.message || error
            }`,
            error,
          };
        }
      }
    }

    // =========================================================================
    // STEP 7: Apply confirm actions (local state only)
    // Backend automatically reduces remainingDecisionQuantity when child orders are created
    // =========================================================================
    workingOrderItems = EstimateAllocatorService.applyConfirmToItems(
      workingOrderItems,
      confirmSelections,
      splitData,
    );

    return {
      success: true,
      message: `Successfully created ${
        createdOrders.length
      } allocated order(s)${remainingOrder ? " and 1 remaining order" : ""}`,
      createdOrders,
      remainingOrder,
      virtualOrderId: allocationRequest.sourceOrder._id,
    };
  }

  /**
   * Validates if the order is blocked from supplier allocation.
   * Checks for CUSTOMER_HOLD, DELIVERED, CANCELLED, FROZEN statuses.
   */
  static validateOrderBlockStatus(orderData: any): VirtualOrderBlockStatus {
    if (orderData.orderStatus === "CUSTOMER_HOLD") {
      return {
        isBlocked: true,
        reason: BLOCK_REASONS.CUSTOMER_HOLD,
        blockType: "CUSTOMER",
      };
    }

    if (orderData.orderStatus === "DELIVERED") {
      return {
        isBlocked: true,
        reason: BLOCK_REASONS.DELIVERED,
        blockType: "SYSTEM",
      };
    }

    if (orderData.orderStatus === "CANCELLED") {
      return {
        isBlocked: true,
        reason: BLOCK_REASONS.CANCELLED,
        blockType: "SYSTEM",
      };
    }

    if (orderData.isCustomerOrderFreezed) {
      return {
        isBlocked: true,
        reason: BLOCK_REASONS.FROZEN,
        blockType: "SYSTEM",
      };
    }

    return { isBlocked: false };
  }

  /**
   * Validates the allocation request.
   * Checks source order data, orderEta, supplier requirements, and delivery options data.
   */
  static validateAllocationRequest(
    allocationRequest: EstimateAllocationConfig,
  ): ValidationResult {
    if (
      !allocationRequest.orderEta ||
      allocationRequest.orderEta.length === 0
    ) {
      return {
        isValid: false,
        message: "Order ETA is required for estimate allocation",
      };
    }

    if (
      !allocationRequest.suppliers ||
      allocationRequest.suppliers.length === 0
    ) {
      return {
        isValid: false,
        message: "At least one supplier is required",
      };
    }

    if (
      !allocationRequest.splitSelections ||
      allocationRequest.splitSelections.length === 0
    ) {
      return {
        isValid: false,
        message: "Split selection data are required for estimate allocation",
      };
    }

    if (
      !allocationRequest.splitData ||
      allocationRequest.splitData.length === 0
    ) {
      return {
        isValid: false,
        message: "Split data is required for estimate allocation",
      };
    }

    if (!allocationRequest.cptGivenTime) {
      return {
        isValid: false,
        message: "CPT given time is required for estimate allocation",
      };
    }

    return { isValid: true };
  }

  /**
   * Maps split parts (from splitData) to order items format.
   * Used for remaining items split to get the exact parts that belong to that split.
   * Matches by partNumber and uses the split's quantity, capped at remainingDecisionQuantity.
   *
   * @param orderItems - Original order items from source order (to get full item data)
   * @param splitParts - Parts from the split (partNumber, quantity)
   * @returns Array of order items with quantities matching the split (capped at remaining)
   */
  static mapSplitPartsToOrderItems(
    orderItems: OrderItem[],
    splitParts: SupplierPartForDelivery[],
  ): OrderItem[] {
    const splitPartsMap = new Map<string, number>();
    for (const part of splitParts) {
      splitPartsMap.set(part.partNumber, part.quantity);
    }

    const mappedItems: OrderItem[] = [];

    for (const item of orderItems) {
      const splitQuantity = splitPartsMap.get(item.partNumber);

      if (splitQuantity && splitQuantity > 0) {
        const remainingQty = item.remainingDecisionQuantity || 0;

        // Guard: Don't allocate more than remainingDecisionQuantity
        if (splitQuantity > remainingQty) {
          console.warn(
            `Split quantity (${splitQuantity}) exceeds remaining (${remainingQty}) for part ${item.partNumber}. Capping allocation to ${remainingQty}.`,
          );
        }

        const allocatedQuantity = Math.min(splitQuantity, remainingQty);

        if (allocatedQuantity > 0) {
          mappedItems.push({
            ...item,
            orderedQuantity: allocatedQuantity,
            remainingDecisionQuantity: allocatedQuantity,
            holdQuantity: 0,
            cancelledQuantity: 0,
          });
        }
      }
    }

    return mappedItems;
  }

  /**
   * Creates a child order for a supplier with the order items.
   * Spreads source order data and sets appropriate fields for child order.
   * Calls placeGaraazOrder with orderHandlerAllocationType=Parent query param.
   */
  static async createChildOrder(
    sourceOrder: any,
    childOrderItems: OrderItem[],
    transactionType: string,
    deliveryCost?: number,
    deliveryMode?: string,
  ): Promise<any> {
    const childOrderPayload: any = {
      ...sourceOrder,
      orderNo: undefined,
      orderStatus: undefined,
      orderType: OrderTypes.REGULAR,
      parentOrderId: sourceOrder._id,
      rootOrderId: sourceOrder._id,
      orderItems: childOrderItems,
      requireEstimate: false,
      allocatedTo: undefined,
      invoiceInfo: undefined,
      challanInfo: undefined,
      deliveryInfo: undefined,
      paymentInfo: undefined,
      shipmentId: undefined,
      orderETA: undefined,
      cptSlab: undefined,
      transactionType: transactionType,
      deliveryCost: deliveryCost,
    };

    if (deliveryMode) {
      childOrderPayload.deliveryMode = deliveryMode;
    }

    const response = await services.placeGaraazOrder(
      childOrderPayload,
      `orderHandlerAllocationType=${OrderHandlerAllocationType.PARENT}`,
    );

    return response;
  }

  /**
   * Updates a child order with financial information from the source order.
   * Includes PPDAmount, orderEta, cptSlab, and for Trading transactions, adds discount and creditNotePercentage.
   * For delivery options confirmations, includes deliveryAmount.
   */
  static async updateOrderInfo(
    orderId: string,
    sourceOrder: any,
    orderEta: any[],
    transactionType?: string,
    discount?: number,
    creditNotePercentage?: number,
    cptSlab?: number,
    vehicleType?: number,
  ): Promise<any> {
    const updatePayload: {
      orderETA: any;
      PPDAmount?: any;
      orderDiscount?: string;
      creditNotePercentage?: string;
      deliveryAmount?: number;
      cptSlabLevel?: number;
    } = {
      orderETA: orderEta,
    };

    if (vehicleType !== undefined) {
      updatePayload.PPDAmount = vehicleType;
    }

    if (transactionType && transactionType === TransactionType.TRADING) {
      updatePayload.orderDiscount = String(discount);
      updatePayload.creditNotePercentage = String(creditNotePercentage);
    }

    if (cptSlab !== undefined) {
      updatePayload.cptSlabLevel = cptSlab;
    }

    const response = await services.updateOrder(orderId, updatePayload);
    return response;
  }

  /**
   * Acknowledges a child order by transitioning it to ACK status.
   * Handles errors gracefully by logging and continuing.
   */
  static async acknowledgeOrder(orderId: string): Promise<any> {
    try {
      const response = await services.checkNextStateOfGaraazOrders(
        orderId,
        OrderStatus.ACK,
      );
      return response;
    } catch (error) {
      console.error("Failed to acknowledge order:", error);
      return null;
    }
  }

  /**
   * Allocates a child order to a supplier.
   */
  static async allocateToSupplier(
    orderId: string,
    supplierId: string,
  ): Promise<any> {
    const allocationPayload: {
      supplierId: string;
    } = {
      supplierId,
    };

    const response = await services.allocateSupplierOrder(
      orderId,
      allocationPayload,
    );

    return response;
  }

  /**
   * Updates the order meta info.
   * Spreads source order's metaInfo first.
   * If includeSupplierAllocationTime is true, adds supplierAllocationTime (for allocated orders).
   */
  static async updateMetaInfo(
    orderId: string,
    metaInfo?: Record<string, any>,
    includeSupplierAllocationTime: boolean = false,
  ): Promise<void> {
    const metaInfoPayload: Record<string, any> = {
      ...(metaInfo || {}),
    };

    if (includeSupplierAllocationTime) {
      metaInfoPayload.supplierAllocationTime = getCurrentUTCTimestamp();
    }

    await services.updateOrderMetaInfo(orderId, metaInfoPayload);
  }

  /**
   * Creates a remaining order for unfulfilled items.
   * Uses createChildOrder logic but with remaining items.
   * Calls updateOrderInfo with financial data and acknowledgeOrder.
   * Updates metaInfo WITHOUT supplierAllocationTime (remaining order is unallocated).
   * Does NOT call allocateToSupplier (remaining order stays unallocated).
   * For delivery options confirmations, includes deliveryCost.
   */
  static async createRemainingOrder(
    sourceOrder: any,
    remainingItems: OrderItem[],
    orderEta: any[],
    transactionType: string,
    discount?: number,
    creditNotePercentage?: number,
    deliveryCost?: number,
    cptSlab?: number,
    remainingVehicleType?: number,
    deliveryMode?: string,
  ): Promise<any> {
    const orderResponse = await EstimateAllocatorService.createChildOrder(
      sourceOrder,
      remainingItems,
      transactionType,
      deliveryCost,
      deliveryMode,
    );

    const orderId = orderResponse._id;

    await EstimateAllocatorService.updateOrderInfo(
      orderId,
      sourceOrder,
      orderEta,
      transactionType,
      discount,
      creditNotePercentage,
      cptSlab,
      remainingVehicleType,
    );

    await EstimateAllocatorService.acknowledgeOrder(orderId);

    await EstimateAllocatorService.updateMetaInfo(
      orderId,
      sourceOrder.metaInfo,
    );
    return orderResponse;
  }

  /**
   * Updates the source order with orderEta.
   * Backend automatically converts to VIRTUAL and updates descendantOrderIds when child orders are created.
   */
  static async updateSourceOrder(
    sourceOrderId: string,
    payload: {
      orderEta?: Array<{ etaGivenTime: string; eta: string; reason?: string }>;
    },
  ): Promise<any> {
    const updatePayload: any = {
      orderETA: payload.orderEta,
    };

    const response = await services.updateOrder(sourceOrderId, updatePayload);

    return response;
  }

  /**
   * @param selections - Filtered selections for a specific action
   * @param splitData - Split data containing parts with partId
   * @returns Array of items with partId and quantity for API call
   */
  static buildActionItemsPayloadFromSelections(
    selections: NormalizedDeliverySelection[],
    splitData: EnrichedSupplierForDelivery[],
  ): Array<{ partId: string; quantity: number }> {
    const selectionMap = new Map(
      selections.map((s) => [s.supplierId ?? "remainingItems", s]),
    );

    const quantityByPartId = new Map<string, number>();

    splitData.forEach((split) => {
      const selection = selectionMap.get(split.supplierId ?? "remainingItems");
      if (!selection) return;

      split.parts.forEach((part) => {
        const partId = part.partId;
        if (!partId) return;

        const current = quantityByPartId.get(partId) || 0;
        quantityByPartId.set(partId, current + part.quantity);
      });
    });

    const items: Array<{ partId: string; quantity: number }> = [];
    quantityByPartId.forEach((quantity, partId) => {
      if (quantity > 0) {
        items.push({ partId, quantity });
      }
    });

    return items;
  }

  /**
   * @param orderNo - Order number
   * @param action - Action type (HOLD, CANCEL, UNHOLD, CONFIRM)
   * @param items - Array of items with partId and quantity
   * @returns Promise with success status and optional error message
   */
  static async executeOrderItemsAction(
    orderNo: string,
    action: OrderItemActionType,
    items: Array<{ partId: string; quantity: number }>,
  ): Promise<{ success: boolean; error?: string }> {
    if (items.length === 0) {
      return { success: true };
    }

    try {
      await services.orderItemsAction(orderNo, action, items);
      return { success: true };
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Generic method to process delivery option selections via the new API.
   * Builds items payload from selections and executes the specified action.
   * Pure functions (applyHoldToItems, applyCancelToItems, applyConfirmToItems)
   * are still used for local state computation.
   *
   * @param orderNo - Order number
   * @param action - Action type (HOLD, CANCEL, CONFIRM)
   * @param selections - Selections from delivery options modal
   * @param splitData - Split data with parts mapping
   * @returns Promise with success status and optional error message
   */
  static async processActionOnOrderItems(
    orderNo: string,
    action: OrderItemActionType,
    selections: NormalizedDeliverySelection[],
    splitData: EnrichedSupplierForDelivery[],
  ): Promise<{ success: boolean; error?: string }> {
    if (selections.length === 0) {
      return { success: true };
    }

    const items =
      EstimateAllocatorService.buildActionItemsPayloadFromSelections(
        selections,
        splitData,
      );

    return EstimateAllocatorService.executeOrderItemsAction(
      orderNo,
      action,
      items,
    );
  }

  // ============================================================================
  // PURE FUNCTIONS (No API calls - for testability and clarity)
  // ============================================================================

  /**
   * Pure function: Applies hold quantities to order items.
   * Updates holdQuantity and reduces remainingDecisionQuantity.
   * Returns items unchanged if holdSelections is empty/undefined.
   *
   * @param orderItems - Current order items (will be cloned, not mutated)
   * @param holdSelections - Hold selections from delivery options modal
   * @param splitData - Split data with parts mapping
   * @returns New array of order items with hold applied
   */
  static applyHoldToItems(
    orderItems: OrderItem[],
    holdSelections: NormalizedDeliverySelection[],
    splitData: EnrichedSupplierForDelivery[],
  ): OrderItem[] {
    if (!holdSelections || holdSelections.length === 0) {
      return orderItems;
    }

    // Map selections to parts by supplierId (null is valid for remaining items)
    const selectionMap = new Map(
      holdSelections.map((s) => [s.supplierId ?? "remainingItems", s]),
    );

    // Build hold quantity updates map: partNumber -> quantity to hold
    const holdUpdates = new Map<string, number>();

    splitData.forEach((split) => {
      const selection = selectionMap.get(split.supplierId ?? "remainingItems");
      if (!selection) return;

      split.parts.forEach((part) => {
        const key = part.partNumber;
        const currentHold = holdUpdates.get(key) || 0;
        holdUpdates.set(key, currentHold + part.quantity);
      });
    });

    // Update order items
    return orderItems.map((item) => {
      const itemPartNumber = item.partNumber;
      const holdQty = holdUpdates.get(itemPartNumber) || 0;

      if (holdQty === 0) return item;

      const currentHold = item.holdQuantity || 0;
      const currentRemaining = item.remainingDecisionQuantity || 0;

      // Validate: can't hold more than remaining
      const finalHoldQty = Math.min(holdQty, currentRemaining);

      return {
        ...item,
        holdQuantity: currentHold + finalHoldQty,
        remainingDecisionQuantity: currentRemaining - finalHoldQty,
      };
    });
  }

  /**
   * Pure function: Applies cancel quantities to order items.
   * Updates cancelledQuantity and reduces remainingDecisionQuantity.
   * Returns items unchanged if cancelSelections is empty/undefined.
   *
   * @param orderItems - Current order items (will be cloned, not mutated)
   * @param cancelSelections - Cancel selections from delivery options modal
   * @param splitData - Split data with parts mapping
   * @returns New array of order items with cancel applied
   */
  static applyCancelToItems(
    orderItems: OrderItem[],
    cancelSelections: NormalizedDeliverySelection[],
    splitData: EnrichedSupplierForDelivery[],
  ): OrderItem[] {
    if (!cancelSelections || cancelSelections.length === 0) {
      return orderItems;
    }

    // Map selections to parts by supplierId (null is valid for remaining items)
    const cancelSelectionMap = new Map(
      cancelSelections.map((s) => [s.supplierId ?? "remainingItems", s]),
    );

    // Build cancel quantity updates map: partNumber -> quantity to cancel
    const cancelUpdates = new Map<string, number>();

    splitData.forEach((split) => {
      const cancelSelection = cancelSelectionMap.get(
        split.supplierId ?? "remainingItems",
      );
      if (!cancelSelection) return;

      split.parts.forEach((part) => {
        const key = part.partNumber;
        const currentCancel = cancelUpdates.get(key) || 0;
        cancelUpdates.set(key, currentCancel + part.quantity);
      });
    });

    // Update order items
    return orderItems.map((item) => {
      const itemPartNumber = item.partNumber;
      const cancelQty = cancelUpdates.get(itemPartNumber) || 0;

      if (cancelQty === 0) return item;

      const currentCancel = item.cancelledQuantity || 0;
      const currentRemaining = item.remainingDecisionQuantity || 0;

      // Validate: can't cancel more than remaining
      const finalCancelQty = Math.min(cancelQty, currentRemaining);

      return {
        ...item,
        cancelledQuantity: currentCancel + finalCancelQty,
        remainingDecisionQuantity: currentRemaining - finalCancelQty,
      };
    });
  }

  /**
   * Pure function: Applies confirm quantities to order items.
   * Reduces remainingDecisionQuantity for each confirmed split (including remaining items where supplierId is null).
   * Does NOT modify orderedQuantity, holdQuantity, or cancelledQuantity.
   * Returns items unchanged if confirmSelections is empty/undefined.
   *
   * @param orderItems - Current order items (will be cloned, not mutated)
   * @param confirmSelections - Confirm selections from delivery options modal
   * @param splitData - Split data with parts mapping
   * @returns New array of order items with confirm applied
   */
  static applyConfirmToItems(
    orderItems: OrderItem[],
    confirmSelections: NormalizedDeliverySelection[],
    splitData: EnrichedSupplierForDelivery[],
  ): OrderItem[] {
    if (!confirmSelections || confirmSelections.length === 0) {
      return orderItems;
    }

    // Map selections to parts by supplierId (null is valid for remaining items)
    const confirmSelectionMap = new Map(
      confirmSelections.map((s) => [s.supplierId ?? "remainingItems", s]),
    );

    // Build confirm quantity updates map: partNumber -> quantity confirmed
    const confirmUpdates = new Map<string, number>();

    splitData.forEach((split) => {
      const confirmSelection = confirmSelectionMap.get(
        split.supplierId ?? "remainingItems",
      );
      if (!confirmSelection) return;

      split.parts.forEach((part) => {
        const key = part.partNumber;
        const currentConfirm = confirmUpdates.get(key) || 0;
        confirmUpdates.set(key, currentConfirm + part.quantity);
      });
    });

    // Update order items - reduce remainingDecisionQuantity for confirmed quantities
    return orderItems.map((item) => {
      const itemPartNumber = item.partNumber;
      const confirmQty = confirmUpdates.get(itemPartNumber) || 0;

      if (confirmQty === 0) return item;

      const currentRemaining = item.remainingDecisionQuantity || 0;

      // Validate: can't confirm more than remaining
      const finalConfirmQty = Math.min(confirmQty, currentRemaining);

      return {
        ...item,
        remainingDecisionQuantity: currentRemaining - finalConfirmQty,
      };
    });
  }

  /**
   * Pure function: Computes updated orderETA with buy time entry.
   * Finds maximum buy time across all selections and creates new ETA entry.
   * Returns unchanged orderEta if buyTimeSelections is empty/undefined or has no valid buy time values.
   *
   * @param orderEta - Current orderETA array
   * @param buyTimeSelections - Buy time selections from delivery options modal
   * @param cptGivenTime - Timestamp when modal was confirmed (ISO string)
   * @returns Object with updatedEta array and isEtaUpdated boolean
   */
  static computeBuyTimeEta(
    orderEta: Array<{ etaGivenTime: string; eta: string; reason?: string }>,
    buyTimeSelections: NormalizedDeliverySelection[],
    cptGivenTime: string,
  ): {
    updatedEta: Array<{ etaGivenTime: string; eta: string; reason?: string }>;
    isEtaUpdated: boolean;
  } {
    if (!buyTimeSelections || buyTimeSelections.length === 0) {
      return { updatedEta: orderEta, isEtaUpdated: false };
    }

    // Filter to only selections with valid buyTimeValue
    const validBuyTimeSelections = buyTimeSelections.filter(
      (s) => s.buyTimeValue,
    );

    if (validBuyTimeSelections.length === 0) {
      return { updatedEta: orderEta, isEtaUpdated: false };
    }

    // Extract buy time values and find maximum (latest datetime)
    const buyTimeValues = validBuyTimeSelections
      .map((s) => new Date(s.buyTimeValue!).getTime())
      .filter((time) => !isNaN(time));

    if (buyTimeValues.length === 0) {
      return { updatedEta: orderEta, isEtaUpdated: false };
    }

    // Find maximum buy time (latest datetime)
    const maxBuyTimeISO = new Date(Math.max(...buyTimeValues)).toISOString();

    // Convert ISO strings to IST format (YYYY-MM-DDTHH:mm:ss) for orderETA
    const maxBuyTimeIST = dayjs(maxBuyTimeISO)
      .utc()
      .tz(IST_TIMEZONE)
      .format("YYYY-MM-DDTHH:mm:ss");
    const cptGivenTimeIST = dayjs(cptGivenTime)
      .utc()
      .tz(IST_TIMEZONE)
      .format("YYYY-MM-DDTHH:mm:ss");

    // Add new ETA entry to existing orderETA array
    const newEta = {
      etaGivenTime: cptGivenTimeIST,
      eta: maxBuyTimeIST,
      reason: "System: Buy Time",
    };

    const updatedEta = [...(orderEta || []), newEta];

    return { updatedEta, isEtaUpdated: true };
  }

  /**
   * Pure function: Applies buy time quantities to order items for allocation tracking.
   * Reduces remainingDecisionQuantity for buy time splits so those quantities
   * are not available for child order allocation.
   * NOTE: This is only used for availableForAllocation, NOT workingOrderItems.
   *
   * @param orderItems - Current order items (will be cloned, not mutated)
   * @param buyTimeSelections - Buy time selections from delivery options modal
   * @param splitData - Split data with parts mapping
   * @returns New array of order items with buy time quantities reduced
   */
  static applyBuyTimeToItems(
    orderItems: OrderItem[],
    buyTimeSelections: NormalizedDeliverySelection[],
    splitData: EnrichedSupplierForDelivery[],
  ): OrderItem[] {
    if (!buyTimeSelections || buyTimeSelections.length === 0) {
      return orderItems;
    }

    // Map selections to parts by supplierId
    const buyTimeSelectionMap = new Map(
      buyTimeSelections.map((s) => [s.supplierId ?? "remainingItems", s]),
    );

    // Build buy time quantity updates map: partNumber -> quantity to reserve
    const buyTimeUpdates = new Map<string, number>();

    splitData.forEach((split) => {
      const buyTimeSelection = buyTimeSelectionMap.get(
        split.supplierId ?? "remainingItems",
      );
      if (!buyTimeSelection) return;

      split.parts.forEach((part) => {
        const key = part.partNumber;
        const currentBuyTimeQuantity = buyTimeUpdates.get(key) || 0;
        buyTimeUpdates.set(key, currentBuyTimeQuantity + part.quantity);
      });
    });

    // Update order items - reduce remainingDecisionQuantity for buy time quantities
    return orderItems.map((item) => {
      const itemPartNumber = item.partNumber;
      const buyTimeQty = buyTimeUpdates.get(itemPartNumber) || 0;

      if (buyTimeQty === 0) return item;

      const currentRemaining = item.remainingDecisionQuantity || 0;

      // Validate: can't buy time more than remaining
      const finalBuyTimeQty = Math.min(buyTimeQty, currentRemaining);

      return {
        ...item,
        remainingDecisionQuantity: currentRemaining - finalBuyTimeQty,
      };
    });
  }

  /**
   * Pure function: Reduces available quantities after an allocation.
   * Used for defensive tracking to prevent over-allocation across multiple suppliers.
   *
   * @param availableItems - Current available items for allocation
   * @param allocatedItems - Items that were just allocated to a child order
   * @returns New array of available items with reduced quantities
   */
  static reduceAvailableItems(
    availableItems: OrderItem[],
    allocatedItems: OrderItem[],
  ): OrderItem[] {
    // Build map of allocated quantities by partNumber
    const allocatedMap = new Map<string, number>();
    for (const item of allocatedItems) {
      const current = allocatedMap.get(item.partNumber) || 0;
      allocatedMap.set(item.partNumber, current + item.orderedQuantity);
    }

    // Reduce available quantities
    return availableItems
      .map((item) => {
        const allocatedQty = allocatedMap.get(item.partNumber) || 0;
        if (allocatedQty === 0) return item;

        const newRemaining = item.remainingDecisionQuantity - allocatedQty;

        return {
          ...item,
          remainingDecisionQuantity: Math.max(0, newRemaining),
        };
      })
      .filter((item) => item.remainingDecisionQuantity > 0);
  }
}
